import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import { BarChart2, X, TrendingUp, Eye, MousePointerClick, Calendar, Banknote, CreditCard, Lightbulb } from 'lucide-react';

import s from './AnalyticsPanel.css';

// GraphQL Query
const GET_FEATURED_ANALYTICS = gql`
  query getFeaturedAnalytics($featuredListingId: Int!, $days: Int) {
    getFeaturedAnalytics(featuredListingId: $featuredListingId, days: $days) {
      totalImpressions
      totalClicks
      totalBookings
      totalRevenue
      ctr
      totalInvested
      roi
      dailyStats {
        date
        impressions
        clicks
        bookings
        revenue
      }
    }
  }
`;

class AnalyticsPanel extends Component {
  static propTypes = {
    featuredListingId: PropTypes.number.isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
  };

  static defaultProps = {
    isOpen: false
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      analytics: null,
      period: 30 // days
    };
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.loadAnalytics();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.loadAnalytics();
    }
  }

  loadAnalytics = async () => {
    const { client, featuredListingId } = this.props;
    const { period } = this.state;

    this.setState({ loading: true });

    try {
      const result = await client.query({
        query: GET_FEATURED_ANALYTICS,
        variables: { featuredListingId, days: period },
        fetchPolicy: 'network-only'
      });

      if (result?.data?.getFeaturedAnalytics) {
        this.setState({
          analytics: result.data.getFeaturedAnalytics,
          loading: false
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      this.setState({ loading: false });
    }
  };

  changePeriod = (newPeriod) => {
    this.setState({ period: newPeriod }, () => {
      this.loadAnalytics();
    });
  };

  formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  renderMiniChart = (dailyStats) => {
    if (!dailyStats || dailyStats.length === 0) {
      return <div className={s.noData}>Sin datos aún</div>;
    }

    // Get max values for scaling
    const maxImpressions = Math.max(...dailyStats.map(d => d.impressions || 0), 1);
    const maxClicks = Math.max(...dailyStats.map(d => d.clicks || 0), 1);

    // Take last 14 days for the chart
    const chartData = dailyStats.slice(-14);

    return (
      <div className={s.chartContainer}>
        <div className={s.chartLegend}>
          <span className={s.legendItem}>
            <span className={s.legendDotBlue}></span>
            Impresiones
          </span>
          <span className={s.legendItem}>
            <span className={s.legendDotGreen}></span>
            Clicks
          </span>
        </div>
        <div className={s.barsContainer}>
          {chartData.map((day, index) => {
            const impressionHeight = (day.impressions / maxImpressions) * 100;
            const clickHeight = (day.clicks / maxClicks) * 100;
            
            return (
              <div key={index} className={s.barGroup} title={`${day.date}: ${day.impressions} imp, ${day.clicks} clicks`}>
                <div 
                  className={cx(s.bar, s.barImpressions)} 
                  style={{ height: `${Math.max(impressionHeight, 4)}%` }}
                />
                <div 
                  className={cx(s.bar, s.barClicks)} 
                  style={{ height: `${Math.max(clickHeight, 4)}%` }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    const { isOpen, onClose, listTitle } = this.props;
    const { loading, analytics, period } = this.state;

    if (!isOpen) return null;

    return (
      <div className={s.overlay} onClick={onClose}>
        <div className={s.panel} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className={s.header}>
            <div>
              <h2 className={s.title}><BarChart2 size={24} /> Analytics</h2>
              {listTitle && <p className={s.subtitle}>{listTitle}</p>}
            </div>
            <button className={s.closeBtn} onClick={onClose}><X size={20} /></button>
          </div>

          {/* Period Selector */}
          <div className={s.periodSelector}>
            {[7, 14, 30].map(p => (
              <button
                key={p}
                className={cx(s.periodBtn, { [s.periodBtnActive]: period === p })}
                onClick={() => this.changePeriod(p)}
              >
                {p} días
              </button>
            ))}
          </div>

          {loading ? (
            <div className={s.loading}>
              <div className={s.spinner}></div>
              <p>Cargando estadísticas...</p>
            </div>
          ) : !analytics ? (
            <div className={s.noData}>
              <p><TrendingUp size={20} /> Aún no hay suficientes datos</p>
              <p className={s.noDataHint}>Las estadísticas aparecerán cuando tu bote reciba impresiones</p>
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className={s.kpiGrid}>
                <div className={s.kpiCard}>
                  <div className={s.kpiIcon}><Eye size={24} /></div>
                  <div className={s.kpiInfo}>
                    <span className={s.kpiValue}>{this.formatNumber(analytics.totalImpressions)}</span>
                    <span className={s.kpiLabel}>Impresiones</span>
                  </div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiIcon}><MousePointerClick size={24} /></div>
                  <div className={s.kpiInfo}>
                    <span className={s.kpiValue}>{this.formatNumber(analytics.totalClicks)}</span>
                    <span className={s.kpiLabel}>Clicks</span>
                  </div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiIcon}><TrendingUp size={24} /></div>
                  <div className={s.kpiInfo}>
                    <span className={s.kpiValue}>{analytics.ctr?.toFixed(2)}%</span>
                    <span className={s.kpiLabel}>CTR</span>
                  </div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiIcon}><Calendar size={24} /></div>
                  <div className={s.kpiInfo}>
                    <span className={s.kpiValue}>{analytics.totalBookings || 0}</span>
                    <span className={s.kpiLabel}>Reservas</span>
                  </div>
                </div>
              </div>

              {/* Revenue & Investment Row */}
              <div className={s.revenueRow}>
                <div className={s.revenueCard}>
                  <span className={s.revenueLabel}><Banknote size={18} /> Ingresos por Reservas</span>
                  <span className={s.revenueValue}>${analytics.totalRevenue?.toFixed(2) || '0.00'}</span>
                  <span className={s.revenueHint}>Dinero generado por reservas</span>
                </div>
                <div className={s.revenueCard}>
                  <span className={s.revenueLabel}><CreditCard size={18} /> Comisiones Generadas</span>
                  <span className={s.revenueValue}>${analytics.totalInvested?.toFixed(2) || '0.00'}</span>
                  <span className={s.revenueHint}>Comisión por reservas featured</span>
                </div>
                <div className={cx(s.revenueCard, analytics.roi > 0 ? s.roiPositive : s.roiNegative)}>
                  <span className={s.revenueLabel}><BarChart2 size={18} /> ROI</span>
                  <span className={s.revenueValue}>
                    {analytics.roi > 0 ? '+' : ''}{analytics.roi?.toFixed(1) || '0'}%
                  </span>
                  <span className={s.revenueHint}>Retorno de inversión</span>
                </div>
              </div>

              {/* Mini Chart */}
              <div className={s.chartSection}>
                <h3 className={s.chartTitle}><TrendingUp size={18} /> Actividad Diaria</h3>
                <p className={s.chartDescription}>
                  Muestra cuántas veces tu bote fue visto (impresiones) y cuántas veces hicieron clic para ver más detalles.
                </p>
                {this.renderMiniChart(analytics.dailyStats)}
              </div>

              {/* Tips */}
              <div className={s.tipsSection}>
                <h4 className={s.tipsTitle}><Lightbulb size={18} /> Consejos</h4>
                {analytics.ctr < 1 && (
                  <p className={s.tip}>• CTR bajo: Considera mejorar las fotos de tu bote</p>
                )}
                {analytics.totalClicks > 0 && analytics.totalBookings === 0 && (
                  <p className={s.tip}>• Tienes clicks pero no reservas: Revisa tus precios o descripción</p>
                )}
                {analytics.roi < 0 && (
                  <p className={s.tip}>• ROI negativo: Ajusta tu bid o espera más reservas</p>
                )}
                {analytics.roi > 100 && (
                  <p className={s.tip}>• ¡Excelente ROI! Tu inversión está generando buenos retornos</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(s),
  withApollo
)(AnalyticsPanel);
